import { type Message } from '@vivport/backend/types';
import { toast } from 'sonner';

export const EXTRACT_QUOTE_HIGHLIGHT_NAME = 'nz-extract-quote';

export function highlightQuoteInMessageBody(quote: string): void {
  const container = document.querySelector('[data-testid="details-message"]');
  if (!container) {
    return;
  }
  if (typeof CSS === 'undefined' || !('highlights' in CSS)) {
    toast.error('Text highlighting is not supported in this browser.');
    return;
  }
  const normalizedQuote = quote.replace(/\s+/g, ' ').trim();
  if (!normalizedQuote) {
    return;
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let normalized = '';
  const charMap: Array<{ node: Text; offset: number }> = [];
  let pendingSpace = false;
  let started = false;
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const text = node.data;
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (/\s/.test(ch)) {
        if (started) {
          pendingSpace = true;
        }
        continue;
      }
      if (pendingSpace) {
        normalized += ' ';
        charMap.push({ node, offset: i });
        pendingSpace = false;
      }
      normalized += ch;
      charMap.push({ node, offset: i });
      started = true;
    }
  }

  const ranges: Range[] = [];
  let searchFrom = 0;
  for (;;) {
    const matchIndex = normalized.indexOf(normalizedQuote, searchFrom);
    if (matchIndex === -1) {
      break;
    }
    const startPos = charMap[matchIndex];
    const endPos = charMap[matchIndex + normalizedQuote.length - 1];
    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset + 1);
    ranges.push(range);
    searchFrom = matchIndex + normalizedQuote.length;
  }

  CSS.highlights.delete(EXTRACT_QUOTE_HIGHLIGHT_NAME);
  if (ranges.length === 0) {
    toast.error('Source text not visible — expand the message containing it.');
    return;
  }
  CSS.highlights.set(EXTRACT_QUOTE_HIGHLIGHT_NAME, new Highlight(...ranges));
  ranges[0].startContainer.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export type ParsedMessage = {
  subject: string | null;
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  provider: 'GMAIL' | 'VIVPORT' | 'OUTLOOK';
  memberExternalAccountId: number | null;
  sentAt: string | null;
  receivedAt: string | null;
  snippet: string | null;
  textBody: string | null;
  htmlBody: string | null;
  providerMessageId: string | null;
  providerThreadId: string | null;
  internetMessageId: string | null;
  inReplyToMessageId: string | null;
  hasAttachments: boolean;
  isDraft: boolean;
  isRead: boolean;
  isStarred: boolean;
  parentCode: string | null;
  rootCode: string | null;
  depth: number;
  sortOrder: number;
  path: string | null;
};

export type MessageConversationItem = NonNullable<Message['conversation']>['items'][number];

export function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

export function isNullableId(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isInteger(value) && value > 0);
}

export function sanitizeHtmlToPlainText(html: string): string {
  if (typeof window === 'undefined') {
    return html;
  }
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocked = doc.querySelectorAll('script, style, iframe, object, embed');
  blocked.forEach((node) => node.remove());
  return (doc.body.textContent ?? '').trim();
}

/** Strips dangerous tags/attributes; returns body HTML safe for dangerouslySetInnerHTML. */
export function sanitizeHtmlForDisplay(html: string): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const doc = new window.DOMParser().parseFromString(html, 'text/html');
  doc
    .querySelectorAll('script, style, iframe, object, embed, form, link[rel="stylesheet"], meta, base')
    .forEach((node) => node.remove());
  doc.body.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if (name === 'srcdoc') {
        el.removeAttribute('srcdoc');
        return;
      }
      if (
        (name === 'href' || name === 'src' || name === 'xlink:href') &&
        /^\s*javascript:/i.test(attr.value)
      ) {
        el.removeAttribute(attr.name);
        return;
      }
      // cid: references point at email-internal attachments the browser cannot
      // fetch; each one logs a network error, so drop the src entirely.
      if ((name === 'src' || name === 'xlink:href') && /^\s*cid:/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
}

export function getExpandedSanitizedHtml(item: MessageConversationItem): string | null {
  const raw = item.htmlBody?.trim();
  if (!raw) {
    return null;
  }
  const sanitized = sanitizeHtmlForDisplay(raw);
  if (typeof window === 'undefined') {
    return null;
  }
  const text = new window.DOMParser().parseFromString(sanitized, 'text/html').body.textContent?.trim() ?? '';
  if (!text) {
    return null;
  }
  return sanitized;
}

export function isQuotedBoundaryNode(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const el = node as HTMLElement;
  if (el.tagName === 'BLOCKQUOTE') {
    return true;
  }
  if (/^(divRplyFwdMsg|appendonsend)$/i.test(el.id)) {
    return true;
  }
  const cls = el.className;
  if (typeof cls === 'string' && /gmail_quote|moz-cite-prefix|yahoo_quoted|OutlookMessageHeader/i.test(cls)) {
    return true;
  }
  if (typeof cls === 'string' && /gmail_signature|moz-signature|outlook_signature/i.test(cls)) {
    return true;
  }
  return false;
}

export function serializeNodeSubset(nodes: readonly Node[]): string {
  if (typeof document === 'undefined') {
    return '';
  }
  const wrap = document.createElement('div');
  for (const n of nodes) {
    wrap.appendChild(n.cloneNode(true));
  }
  return wrap.innerHTML;
}

export function trySplitContainerChildren(container: HTMLElement): { main: string; quoted: string } | null {
  const nodes = Array.from(container.childNodes);
  const idx = nodes.findIndex((n) => isQuotedBoundaryNode(n));
  if (idx <= 0) {
    return null;
  }
  const mainHtml = serializeNodeSubset(nodes.slice(0, idx));
  const quotedHtml = serializeNodeSubset(nodes.slice(idx));
  if (!quotedHtml.trim()) {
    return null;
  }
  const quotedText =
    typeof window !== 'undefined'
      ? new DOMParser().parseFromString(quotedHtml, 'text/html').body.textContent?.trim() ?? ''
      : quotedHtml.replace(/<[^>]+>/g, '').trim();
  if (!quotedText) {
    return null;
  }
  return { main: mainHtml.trim(), quoted: quotedHtml.trim() };
}

/** Thread / blockquote / gmail_quote only (no signature). */
export function splitHtmlThreadOnly(html: string): { main: string; quoted: string | null } {
  if (typeof window === 'undefined' || !html.trim()) {
    return { main: html, quoted: null };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  const top = trySplitContainerChildren(body);
  if (top) {
    return { main: top.main, quoted: top.quoted };
  }

  if (body.children.length === 1) {
    const only = body.children[0];
    if (only instanceof HTMLElement) {
      const inner = trySplitContainerChildren(only);
      if (inner && inner.main.trim()) {
        const wrapped = only.cloneNode(false) as HTMLElement;
        wrapped.innerHTML = inner.main;
        return { main: wrapped.outerHTML, quoted: inner.quoted };
      }
    }
  }

  return { main: html, quoted: null };
}

export function quotedHtmlHasText(quotedHtml: string): boolean {
  if (typeof window === 'undefined') {
    return quotedHtml.replace(/<[^>]+>/g, '').trim().length > 0;
  }
  return (new DOMParser().parseFromString(quotedHtml, 'text/html').body.textContent?.trim().length ?? 0) > 0;
}

/** Split after Gmail / client signature block when it is a sibling under body or under a single wrapper. */
export function splitHtmlAtGmailSignature(html: string): { main: string; quoted: string | null } {
  if (typeof window === 'undefined' || !html.trim()) {
    return { main: html, quoted: null };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  const sig = body.querySelector(
    '.gmail_signature, [class*="gmail_signature"], [data-smartmail="gmail_signature"], .moz-signature'
  );
  if (!sig) {
    return { main: html, quoted: null };
  }

  const splitAtBodyLevel = (): { main: string; quoted: string } | null => {
    let splitRoot: Node = sig;
    while (splitRoot.parentNode && splitRoot.parentNode !== body) {
      splitRoot = splitRoot.parentNode;
    }
    if (splitRoot.parentNode !== body) {
      return null;
    }
    const nodes = Array.from(body.childNodes);
    const idx = nodes.indexOf(splitRoot as ChildNode);
    if (idx <= 0) {
      return null;
    }
    const mainHtml = serializeNodeSubset(nodes.slice(0, idx));
    const quotedHtml = serializeNodeSubset(nodes.slice(idx));
    if (!quotedHtml.trim() || !quotedHtmlHasText(quotedHtml)) {
      return null;
    }
    return { main: mainHtml.trim(), quoted: quotedHtml.trim() };
  };

  const splitInsideWrapper = (wrap: HTMLElement): { main: string; quoted: string } | null => {
    const innerSig = wrap.querySelector(
      '.gmail_signature, [class*="gmail_signature"], [data-smartmail="gmail_signature"], .moz-signature'
    );
    if (!innerSig) {
      return null;
    }
    let splitRoot: Node = innerSig;
    while (splitRoot.parentNode && splitRoot.parentNode !== wrap) {
      splitRoot = splitRoot.parentNode;
    }
    if (splitRoot.parentNode !== wrap) {
      return null;
    }
    const innerNodes = Array.from(wrap.childNodes);
    const j = innerNodes.indexOf(splitRoot as ChildNode);
    if (j <= 0) {
      return null;
    }
    const mainInner = serializeNodeSubset(innerNodes.slice(0, j));
    const quotedInner = serializeNodeSubset(innerNodes.slice(j));
    if (!quotedInner.trim() || !quotedHtmlHasText(quotedInner)) {
      return null;
    }
    const clone = wrap.cloneNode(false) as HTMLElement;
    clone.innerHTML = mainInner.trim();
    return { main: clone.outerHTML.trim(), quoted: quotedInner.trim() };
  };

  const top = splitAtBodyLevel();
  if (top) {
    return { main: top.main, quoted: top.quoted };
  }
  if (body.children.length === 1) {
    const only = body.children[0];
    if (only instanceof HTMLElement) {
      const inner = splitInsideWrapper(only);
      if (inner) {
        return { main: inner.main, quoted: inner.quoted };
      }
    }
  }
  return { main: html, quoted: null };
}

export function splitHtmlMainAndQuoted(html: string): { main: string; quoted: string | null } {
  const thread = splitHtmlThreadOnly(html);
  if (thread.quoted) {
    return thread;
  }
  const sig = splitHtmlAtGmailSignature(thread.main);
  if (sig.quoted) {
    return sig;
  }
  return thread;
}

export const PLAIN_QUOTED_SPLITTERS: RegExp[] = [
  /\r?\nOn .{5,400}?wrote:\s*\r?\n/i,
  /\r?\n-{2,}\s*Original Message\s*-{2,}\s*\r?\n/i,
  /\r?\nFrom:\s.+\r?\nSent:\s.+\r?\nTo:\s/i,
  /\r?\n_{10,}\r?\n/,
];

export function splitPlainMainAndQuoted(text: string): { main: string; quoted: string | null } {
  const t = text.trim();
  if (!t) {
    return { main: text, quoted: null };
  }
  for (const re of PLAIN_QUOTED_SPLITTERS) {
    const m = re.exec(t);
    if (m && m.index > 0) {
      const main = t.slice(0, m.index).trimEnd();
      const quoted = t.slice(m.index).trimStart();
      if (main.length > 0 && quoted.length > 0) {
        return { main, quoted };
      }
    }
  }
  return { main: text, quoted: null };
}

export function plainLooksLikeForwardBlock(s: string): boolean {
  return /^On .{5,400}?wrote:/im.test(s) || /^From:\s.+\nSent:/im.test(s);
}

/** Trailing signature / boilerplate after the visible reply (often only single newlines before name/title lines). */
export function splitPlainTrailingSignature(text: string): { main: string; quoted: string | null } {
  const t = text.replace(/\r\n/g, '\n').trimEnd();
  if (t.length < 50) {
    return { main: t, quoted: null };
  }

  const tailFirstLineLooksLikeBodyContinuation = (tail: string): boolean => {
    const first = tail.split('\n').find((ln) => ln.trim().length > 0) ?? '';
    const f = first.trim();
    return /^(Could|Can|Please|Would|I |Hi |Hey |Hello |Thanks |Thank you)/i.test(f);
  };

  const reNn = /\n\n+/g;
  let m: RegExpExecArray | null;
  const nnMatches: RegExpExecArray[] = [];
  while ((m = reNn.exec(t)) !== null) {
    nnMatches.push(m);
  }
  for (let i = nnMatches.length - 1; i >= 0; i--) {
    const match = nnMatches[i]!;
    const idx = match.index!;
    const after = t.slice(idx + match[0].length).trim();
    const before = t.slice(0, idx).trimEnd();
    if (before.length < 35 || after.length < 12) {
      continue;
    }
    if (after.length > 900) {
      continue;
    }
    if (plainLooksLikeForwardBlock(after)) {
      continue;
    }
    if (tailFirstLineLooksLikeBodyContinuation(after)) {
      continue;
    }
    const afterLines = after.split('\n').filter((ln) => ln.trim().length > 0).length;
    if (afterLines > 14) {
      continue;
    }
    return { main: before, quoted: after };
  }

  const lines = t.split('\n');
  if (lines.length < 4) {
    return { main: t, quoted: null };
  }
  const maxN = Math.min(8, lines.length - 2);
  for (let n = maxN; n >= 2; n--) {
    const tail = lines.slice(-n);
    const head = lines.slice(0, -n);
    const quoted = tail.join('\n').trim();
    const main = head.join('\n').trimEnd();
    if (main.length < 35 || quoted.length < 12) {
      continue;
    }
    if (quoted.length > 700) {
      continue;
    }
    if (plainLooksLikeForwardBlock(quoted)) {
      continue;
    }
    if (tailFirstLineLooksLikeBodyContinuation(quoted)) {
      continue;
    }
    if (tail.some((ln) => ln.length > 160)) {
      continue;
    }
    const nonemptyTail = tail.filter((ln) => ln.trim().length > 0);
    if (nonemptyTail.length < 2) {
      continue;
    }
    return { main, quoted };
  }

  return { main: t, quoted: null };
}

export type ExpandedBodyParts =
  | { mode: 'html'; main: string; quoted: string | null }
  | { mode: 'markdown'; main: string; quoted: string | null };

export function getExpandedBodyParts(
  sanitizedHtml: string | null,
  readableBody: string | null
): ExpandedBodyParts | null {
  if (sanitizedHtml) {
    const { main, quoted } = splitHtmlMainAndQuoted(sanitizedHtml);
    return { mode: 'html', main, quoted };
  }
  const plain = readableBody?.trim() ?? '';
  if (!plain) {
    return null;
  }
  const thread = splitPlainMainAndQuoted(plain);
  if (thread.quoted) {
    return { mode: 'markdown', main: thread.main, quoted: thread.quoted };
  }
  const sig = splitPlainTrailingSignature(thread.main);
  return { mode: 'markdown', main: sig.main, quoted: sig.quoted };
}


/**
 * Center only "rich" email layouts (table/image-heavy marketing templates),
 * while keeping normal HTML emails in standard left-aligned flow.
 */
export function shouldCenterRichHtmlBlock(html: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const tableCount = body.querySelectorAll('table').length;
  if (tableCount === 0) {
    return false;
  }
  const imageCount = body.querySelectorAll('img').length;
  if (imageCount >= 2) {
    return true;
  }
  // Many newsletter/email-builder templates rely on explicit widths.
  if (body.querySelector('[width], [style*="width:"]')) {
    return true;
  }
  return false;
}

export function isRichLayoutBoundaryNode(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const el = node as HTMLElement;
  if (el.tagName === 'TABLE') {
    return true;
  }
  if (el.querySelector('table')) {
    return true;
  }
  // Image-heavy wrappers with explicit sizing often indicate template sections.
  if (el.querySelectorAll('img').length >= 2 && el.querySelector('[width], [style*="width:"]')) {
    return true;
  }
  return false;
}

/**
 * Split HTML into leading free-form text and first rich/template block (if any).
 * This keeps forwarded/plain preface left-aligned while centering the layout block.
 */
export function splitHtmlAtFirstRichLayoutBoundary(html: string): { leadHtml: string; richHtml: string | null } {
  if (typeof window === 'undefined' || !html.trim()) {
    return { leadHtml: html, richHtml: null };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  const splitAtContainer = (container: HTMLElement): { leadHtml: string; richHtml: string } | null => {
    const nodes = Array.from(container.childNodes);
    const idx = nodes.findIndex((node) => isRichLayoutBoundaryNode(node));
    if (idx <= 0) {
      return null;
    }
    const leadHtml = serializeNodeSubset(nodes.slice(0, idx)).trim();
    const richHtml = serializeNodeSubset(nodes.slice(idx)).trim();
    if (!richHtml) {
      return null;
    }
    return { leadHtml, richHtml };
  };

  const top = splitAtContainer(body);
  if (top) {
    return top;
  }

  if (body.children.length === 1) {
    const only = body.children[0];
    if (only instanceof HTMLElement) {
      const inner = splitAtContainer(only);
      if (inner && inner.richHtml.trim()) {
        const leadWrap = only.cloneNode(false) as HTMLElement;
        leadWrap.innerHTML = inner.leadHtml;
        const richWrap = only.cloneNode(false) as HTMLElement;
        richWrap.innerHTML = inner.richHtml;
        return {
          leadHtml: leadWrap.innerHTML.trim() ? leadWrap.outerHTML : '',
          richHtml: richWrap.outerHTML,
        };
      }
    }
  }

  return { leadHtml: html, richHtml: null };
}

export function parseMessage(content: Message): { parsed: ParsedMessage | null; errors: string[] } {
  const errors: string[] = [];

  if (!isNullableString(content.subject)) errors.push('subject must be string or null.');
  if (!isNullableString(content.sentAt)) errors.push('sentAt must be string or null.');
  if (!isNullableString(content.receivedAt)) errors.push('receivedAt must be string or null.');
  if (!isNullableString(content.snippet)) errors.push('snippet must be string or null.');
  if (!isNullableString(content.textBody)) errors.push('textBody must be string or null.');
  if (!isNullableString(content.htmlBody)) errors.push('htmlBody must be string or null.');
  if (!isNullableString(content.providerMessageId)) errors.push('providerMessageId must be string or null.');
  if (!isNullableString(content.providerThreadId)) errors.push('providerThreadId must be string or null.');
  if (!isNullableString(content.internetMessageId)) errors.push('internetMessageId must be string or null.');
  if (!isNullableString(content.inReplyToMessageId)) errors.push('inReplyToMessageId must be string or null.');
  if (!isNullableId(content.memberExternalAccountId)) {
    errors.push('memberExternalAccountId must be positive integer or null.');
  }
  if (!isNullableString(content.parentCode)) errors.push('parentCode must be string or null.');
  if (!isNullableString(content.rootCode)) errors.push('rootCode must be string or null.');
  if (!Number.isInteger(content.depth) || content.depth < 0) errors.push('depth must be a non-negative integer.');
  if (!Number.isInteger(content.sortOrder)) errors.push('sortOrder must be an integer.');
  if (!(content.path === null || typeof content.path === 'string')) errors.push('path must be string or null.');

  if (content.sentAt !== null && Number.isNaN(new Date(content.sentAt).getTime())) {
    errors.push(`sentAt is not a valid ISO timestamp: ${content.sentAt}`);
  }
  if (content.receivedAt !== null && Number.isNaN(new Date(content.receivedAt).getTime())) {
    errors.push(`receivedAt is not a valid ISO timestamp: ${content.receivedAt}`);
  }

  if (errors.length > 0) {
    return { parsed: null, errors };
  }

  return {
    parsed: {
      subject: content.subject,
      direction: content.direction,
      provider: content.provider,
      memberExternalAccountId: content.memberExternalAccountId,
      sentAt: content.sentAt,
      receivedAt: content.receivedAt,
      snippet: content.snippet,
      textBody: content.textBody,
      htmlBody: content.htmlBody,
      providerMessageId: content.providerMessageId,
      providerThreadId: content.providerThreadId,
      internetMessageId: content.internetMessageId,
      inReplyToMessageId: content.inReplyToMessageId,
      hasAttachments: content.hasAttachments,
      isDraft: content.isDraft,
      isRead: content.isRead,
      isStarred: content.isStarred,
      parentCode: content.parentCode,
      rootCode: content.rootCode,
      depth: content.depth,
      sortOrder: content.sortOrder,
      path: content.path,
    },
    errors: [],
  };
}

export function joinConversationAddresses(
  addresses: Array<{ email: string; name: string | null }>
): string {
  if (addresses.length === 0) {
    return 'N/A';
  }
  return addresses
    .map((entry) => (entry.name && entry.name.trim().length > 0 ? `${entry.name} <${entry.email}>` : entry.email))
    .join(', ');
}

export function formatReadableTimestamp(value: string | null): string {
  if (!value) {
    return 'Unknown time';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getConversationTimelineAt(item: MessageConversationItem): string {
  return item.sentAt ?? item.receivedAt ?? item.timelineAt ?? item.originCreatedAt;
}

export function getReadableMessageBody(item: MessageConversationItem): string | null {
  const derivedAuthoredBody = item.derivedAuthoredBody?.trim();
  if (derivedAuthoredBody && derivedAuthoredBody.length > 0) {
    return derivedAuthoredBody;
  }
  const derivedReadableBody = item.derivedReadableBody?.trim();
  if (derivedReadableBody && derivedReadableBody.length > 0) {
    return derivedReadableBody;
  }
  const textBody = item.textBody?.trim();
  if (textBody && textBody.length > 0) {
    return textBody;
  }
  if (item.htmlBody) {
    const sanitized = sanitizeHtmlToPlainText(item.htmlBody);
    if (sanitized.length > 0) {
      return sanitized;
    }
  }
  const summary = item.collapsedSummary.trim();
  if (summary.length > 0) {
    return summary;
  }
  return item.snippet?.trim() || null;
}

export function getDisplayName(item: MessageConversationItem): string {
  return item.from?.name?.trim() || item.from?.email || 'Unknown sender';
}

export function getAvatarText(item: MessageConversationItem): string {
  const base = item.from?.name?.trim() || item.from?.email?.trim() || '?';
  return base.charAt(0).toUpperCase();
}

export function getMemberAvatarText(email: string | null | undefined): string {
  const base = email?.trim();
  if (!base) {
    return '?';
  }
  return base.charAt(0).toUpperCase();
}

export function getRecipientSummary(item: MessageConversationItem): string | null {
  if (item.to.length === 0) {
    return null;
  }
  const names = item.to.map((entry) => entry.name?.trim() || entry.email).filter(Boolean);
  if (names.length === 0) {
    return null;
  }
  return `to ${names.join(', ')}`;
}

export function getCollapsedPreview(item: MessageConversationItem): string {
  const body = getReadableMessageBody(item) ?? item.collapsedSummary ?? item.snippet ?? '';
  const compact = body.replace(/\s+/g, ' ').trim();
  if (!compact) {
    return 'No preview available.';
  }
  return compact.length > 170 ? `${compact.slice(0, 170)}...` : compact;
}

export function getSenderMailDomain(senderEmail: string | null): string | null {
  if (!senderEmail?.includes('@')) {
    return null;
  }
  const domain = senderEmail.split('@').pop()?.trim().toLowerCase();
  return domain && domain.length > 0 ? domain : null;
}

export function resolveConversationSourceItem(content: Message): MessageConversationItem | null {
  const conversation = content.conversation;
  if (!conversation || !Array.isArray(conversation.items) || conversation.items.length === 0) {
    return null;
  }
  return (
    conversation.items.find((item) => item.id === conversation.sourceMessageId) ??
    conversation.items.find((item) => item.id === content.id) ??
    null
  );
}
